import React, { useState } from "react";
import { X, Save, UserPlus, AlertCircle, Wallet } from "lucide-react";
import { supabase } from "../Services/supabaseClient";
import { useCompany } from "../context/CompanyContext";
import "./add-customer-modal.css";

export default function AddCustomerModal({ onClose, onAdd }) {
  const { companyId } = useCompany();

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [address, setAddress] = useState("");
  const [phone, setPhone] = useState("");
  const [currency, setCurrency] = useState("NGN");
  const [openBalance, setOpenBalance] = useState(0);
  const [errorMessage, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage("");

    if (!companyId) {
      setErrorMessage("No company selected. Please reload and try again.");
      return;
    }

    setLoading(true);
    let createdCustomer = null;

    try {
      const {
        data: { user },
        error: authErr,
      } = await supabase.auth.getUser();

      if (authErr || !user) throw new Error("Not authenticated.");

      const ob = Number(openBalance || 0);
      const obIsPositive = Number.isFinite(ob) && ob > 0;

      if (obIsPositive) {
        const ensure = await supabase.rpc("ensure_company_trade_settings", {
          p_company_id: companyId,
        });
        if (ensure.error) throw new Error(ensure.error.message);
      }

      const { data, error } = await supabase
        .from("customers")
        .insert([
          {
            company_id: companyId,
            name: firstName.trim(),
            last_name: lastName.trim(),
            company_name: companyName.trim(),
            address: address.trim(),
            phone: phone.trim(),
            currency,
            open_balance: ob || 0,
          },
        ])
        .select()
        .single();

      if (error) throw new Error("Could not add customer. Please try again.");

      createdCustomer = data;

      if (obIsPositive) {
        const { data: ts, error: tsErr } = await supabase
          .from("company_trade_settings")
          .select("ar_account_id, sales_revenue_account_id")
          .eq("company_id", companyId)
          .single();

        if (tsErr || !ts) {
          throw new Error("Missing company trade settings for AR and revenue.");
        }

        if (!ts.ar_account_id)
          throw new Error("Trade settings missing AR account.");
        if (!ts.sales_revenue_account_id) {
          throw new Error("Trade settings missing sales revenue account.");
        }

        const desc =
          `Customer Opening Balance - ${firstName.trim()} ${lastName.trim()}`.trim();

        const { error: jErr } = await supabase.from("journal_entries").insert([
          {
            company_id: companyId,
            account_id: ts.ar_account_id,
            date: new Date().toISOString().slice(0, 10),
            description: desc,
            money_in: ob,
            money_out: 0,
            source_type: "customer_opening_balance",
            source_id: createdCustomer.id,
            created_by: user.id,
          },
          {
            company_id: companyId,
            account_id: ts.sales_revenue_account_id,
            date: new Date().toISOString().slice(0, 10),
            description: desc,
            money_in: 0,
            money_out: ob,
            source_type: "customer_opening_balance",
            source_id: createdCustomer.id,
            created_by: user.id,
          },
        ]);

        if (jErr)
          throw new Error(jErr.message || "Failed to post opening balance.");
      }

      onAdd(createdCustomer);
      onClose();
    } catch (err) {
      console.error("AddCustomerModal submit error:", err);

      if (createdCustomer?.id) {
        try {
          await supabase
            .from("customers")
            .delete()
            .eq("company_id", companyId)
            .eq("id", createdCustomer.id);
        } catch (rbErr) {
          console.warn("Rollback failed:", rbErr);
        }
      }

      setErrorMessage(
        err?.message || "Could not add customer. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="acm-overlay" role="dialog" aria-modal="true">
      <div className="acm-modal">
        <header className="acm-head">
          <div className="acm-title-wrap">
            <div className="acm-icon">
              <UserPlus size={20} />
            </div>
            <div>
              <p className="acm-eyebrow">Customer Setup</p>
              <h2>Add New Customer</h2>
              <p>
                Create a customer profile for invoices, receipts, payments and
                receivables.
              </p>
            </div>
          </div>

          <button
            type="button"
            className="acm-close"
            aria-label="Close"
            onClick={onClose}
            disabled={loading}
          >
            <X size={18} />
          </button>
        </header>

        {errorMessage && (
          <div className="acm-banner error">
            <AlertCircle size={16} />
            {errorMessage}
          </div>
        )}

        <form onSubmit={handleSubmit} className="acm-form">
          <section className="acm-section">
            <h3>Basic Information</h3>

            <div className="acm-grid two">
              <label>
                First Name
                <input
                  type="text"
                  placeholder="Ada"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  required
                  disabled={loading}
                />
              </label>

              <label>
                Last Name
                <input
                  type="text"
                  placeholder="Okafor"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  required
                  disabled={loading}
                />
              </label>
            </div>

            <label>
              Company Name
              <input
                type="text"
                placeholder="KostPadi Ltd."
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                required
                disabled={loading}
              />
            </label>

            <label>
              Address
              <input
                type="text"
                placeholder="12 Admiralty Way, Lekki Phase 1, Lagos"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                required
                disabled={loading}
              />
            </label>
          </section>

          <section className="acm-section">
            <h3>Contact & Opening Balance</h3>

            <div className="acm-grid three">
              <label>
                Phone
                <input
                  type="text"
                  placeholder="+234 801 234 5678"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  required
                  disabled={loading}
                />
              </label>

              <label>
                Currency
                <select
                  value={currency}
                  onChange={(e) => setCurrency(e.target.value)}
                  required
                  disabled={loading}
                >
                  <option value="NGN">NGN</option>
                  <option value="USD">USD</option>
                  <option value="GBP">GBP</option>
                </select>
              </label>

              <label>
                Opening Balance
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="0.00"
                  value={openBalance}
                  onChange={(e) => setOpenBalance(e.target.value)}
                  disabled={loading}
                />
              </label>
            </div>

            <div className="acm-help">
              <Wallet size={16} />
              Opening balance means the amount this customer already owed before
              you started using KostPadi.
            </div>
          </section>

          <div className="acm-actions">
            <button
              type="button"
              className="acm-btn ghost"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </button>

            <button
              type="submit"
              className="acm-btn primary"
              disabled={loading}
            >
              <Save size={16} />
              {loading ? "Adding Customer…" : "Add Customer"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
